import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { shopifyService } from "@/services/shopify";
import { Loader2, CheckCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";

interface ShopifyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  brandId: string;
}

interface ShopifySettings {
  url: string;
  accessToken: string;
}

interface ShopifyBlog {
  id: number;
  title: string;
  handle: string;
}

interface ConnectionSettings {
  defaultBlogId: string;
  autoPublish: boolean;
  defaultAuthor: string;
  defaultTags: string;
}

enum ConnectionStep {
  CREDENTIALS = "credentials",
  BLOG_SELECTION = "blog_selection",
  CONNECTED = "connected",
}

export function ShopifyDialog({
  open,
  onOpenChange,
  onSuccess,
  brandId,
}: ShopifyDialogProps) {
  const [currentStep, setCurrentStep] = useState<ConnectionStep>(
    ConnectionStep.CREDENTIALS,
  );
  const [loading, setLoading] = useState(false);
  const [shopifySettings, setShopifySettings] = useState<ShopifySettings>({
    url: "",
    accessToken: "",
  });
  const [availableBlogs, setAvailableBlogs] = useState<ShopifyBlog[]>([]);
  const [connectionSettings, setConnectionSettings] =
    useState<ConnectionSettings>({
      defaultBlogId: "",
      autoPublish: true,
      defaultAuthor: "",
      defaultTags: "",
    });

  const handleTestConnection = async () => {
    if (!brandId) {
      toast.error("Please select a brand first");
      return;
    }

    setLoading(true);
    try {
      // Test Shopify connection and get blogs using service
      const data = await shopifyService.testConnection({
        brandId,
        ...shopifySettings,
      });

      setAvailableBlogs(data.blogs);

      // Auto-select blog if only one exists
      if (data.blogs.length === 1) {
        setConnectionSettings((prev) => ({
          ...prev,
          defaultBlogId: data.blogs[0].id.toString(),
        }));
      }

      setCurrentStep(ConnectionStep.BLOG_SELECTION);
      toast.success("Connection successful! Please select your blog.");
    } catch (error: any) {
      console.error("Error testing Shopify connection:", error);
      toast.error(error.message || "Failed to connect to Shopify");
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteConnection = async () => {
    if (!connectionSettings.defaultBlogId) {
      toast.error("Please select a blog");
      return;
    }

    setLoading(true);
    try {
      // Complete Shopify connection with blog selection using service
      await shopifyService.connect({
        brandId,
        ...shopifySettings,
        ...connectionSettings,
      });

      setCurrentStep(ConnectionStep.CONNECTED);
      toast.success("Shopify integration setup complete!");

      // Close dialog after a short delay to show success state
      setTimeout(() => {
        onOpenChange(false);
        onSuccess();
        resetDialog();
      }, 2000);
    } catch (error: any) {
      console.error("Error completing Shopify connection:", error);
      toast.error(error.message || "Failed to complete Shopify setup");
    } finally {
      setLoading(false);
    }
  };

  const resetDialog = () => {
    setCurrentStep(ConnectionStep.CREDENTIALS);
    setShopifySettings({ url: "", accessToken: "" });
    setAvailableBlogs([]);
    setConnectionSettings({
      defaultBlogId: "",
      autoPublish: true,
      defaultAuthor: "",
      defaultTags: "",
    });
  };

  const handleBack = () => {
    if (currentStep === ConnectionStep.BLOG_SELECTION) {
      setCurrentStep(ConnectionStep.CREDENTIALS);
      setAvailableBlogs([]);
    }
  };

  const getSelectedBlog = () => {
    return availableBlogs.find(
      (blog) => blog.id.toString() === connectionSettings.defaultBlogId,
    );
  };

  const renderCredentialsStep = () => (
    <>
      <DialogHeader>
        <DialogTitle>Connect Shopify</DialogTitle>
        <DialogDescription>
          Enter your Shopify credentials to connect your store
        </DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-2 gap-6">
        {/* Instructions */}
        <div className="space-y-4">
          <div className="rounded-lg border p-4 bg-slate-50">
            <h4 className="font-medium mb-2">
              How to generate Shopify Access Token:
            </h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Log in to your Shopify admin dashboard</li>
              <li>Go to Apps → App and sales channel settings</li>
              <li>Click "Develop apps" at the bottom</li>
              <li>Create a new app or select an existing one</li>
              <li>Go to "API credentials" tab</li>
              <li>Click "Configure Admin API scopes"</li>
              <li>
                Select the following scopes:
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li>read_content, write_content</li>
                  <li>read_themes, write_themes</li>
                </ul>
              </li>
              <li>Save and install the app in your store</li>
              <li>Copy the Admin API access token</li>
            </ol>
          </div>
        </div>

        {/* Connection Form */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="url">Shopify Admin URL</Label>
            <Input
              id="url"
              placeholder="https://your-store.myshopify.com/admin"
              value={shopifySettings.url}
              onChange={(e) =>
                setShopifySettings((prev) => ({
                  ...prev,
                  url: e.target.value,
                }))
              }
            />
            <p className="text-xs text-muted-foreground">
              Make sure to include /admin at the end
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="accessToken">Access Token</Label>
            <Input
              id="accessToken"
              type="password"
              placeholder="Shopify Admin API access token"
              value={shopifySettings.accessToken}
              onChange={(e) =>
                setShopifySettings((prev) => ({
                  ...prev,
                  accessToken: e.target.value,
                }))
              }
            />
            <p className="text-sm text-muted-foreground">
              This is your Shopify Admin API access token. See instructions on
              the left.
            </p>
          </div>
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button
          onClick={handleTestConnection}
          disabled={
            loading || !shopifySettings.url || !shopifySettings.accessToken
          }
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Testing Connection...
            </>
          ) : (
            "Test Connection"
          )}
        </Button>
      </DialogFooter>
    </>
  );

  const renderBlogSelectionStep = () => (
    <>
      <DialogHeader>
        <DialogTitle>Configure Shopify Integration</DialogTitle>
        <DialogDescription>
          Choose your default blog and publishing settings
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-6">
        {/* Connection Status */}
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Successfully connected to your Shopify store! Found{" "}
            {availableBlogs.length} blog{availableBlogs.length !== 1 ? "s" : ""}
            .
          </AlertDescription>
        </Alert>

        {/* Blog Selection */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="blog-select">Default Blog</Label>
            <Select
              value={connectionSettings.defaultBlogId}
              onValueChange={(value) =>
                setConnectionSettings((prev) => ({
                  ...prev,
                  defaultBlogId: value,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a blog for publishing articles" />
              </SelectTrigger>
              <SelectContent>
                {availableBlogs.map((blog) => (
                  <SelectItem key={blog.id} value={blog.id.toString()}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{blog.title}</span>
                      <span className="text-sm text-muted-foreground">
                        /{blog.handle}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Articles will be published to this blog by default
            </p>
          </div>

          <Separator />

          {/* Publishing Settings */}
          <div className="space-y-4">
            <h4 className="font-medium">Publishing Settings</h4>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-publish">Auto-publish articles</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically publish articles to Shopify when they're created
                </p>
              </div>
              <Switch
                id="auto-publish"
                checked={connectionSettings.autoPublish}
                onCheckedChange={(checked) =>
                  setConnectionSettings((prev) => ({
                    ...prev,
                    autoPublish: checked,
                  }))
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="default-author">
                  Default Author (Optional)
                </Label>
                <Input
                  id="default-author"
                  placeholder="e.g., Store Admin"
                  value={connectionSettings.defaultAuthor}
                  onChange={(e) =>
                    setConnectionSettings((prev) => ({
                      ...prev,
                      defaultAuthor: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="default-tags">Default Tags (Optional)</Label>
                <Input
                  id="default-tags"
                  placeholder="e.g., blog, news"
                  value={connectionSettings.defaultTags}
                  onChange={(e) =>
                    setConnectionSettings((prev) => ({
                      ...prev,
                      defaultTags: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={handleBack}>
          Back
        </Button>
        <Button
          onClick={handleCompleteConnection}
          disabled={loading || !connectionSettings.defaultBlogId}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Setting up...
            </>
          ) : (
            "Complete Setup"
          )}
        </Button>
      </DialogFooter>
    </>
  );

  const renderConnectedStep = () => (
    <>
      <DialogHeader>
        <DialogTitle>Shopify Connected Successfully!</DialogTitle>
        <DialogDescription>
          Your Shopify integration is now set up and ready to use
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Your articles will now be automatically published to{" "}
            <strong>{getSelectedBlog()?.title}</strong>
            {connectionSettings.autoPublish
              ? " when created."
              : " when you manually publish them."}
          </AlertDescription>
        </Alert>

        <div className="rounded-lg border p-4 bg-slate-50 space-y-2">
          <h4 className="font-medium">Integration Settings</h4>
          <div className="text-sm space-y-1">
            <p>
              <strong>Default Blog:</strong> {getSelectedBlog()?.title}
            </p>
            <p>
              <strong>Auto-publish:</strong>{" "}
              {connectionSettings.autoPublish ? "Enabled" : "Disabled"}
            </p>
            {connectionSettings.defaultAuthor && (
              <p>
                <strong>Default Author:</strong>{" "}
                {connectionSettings.defaultAuthor}
              </p>
            )}
            {connectionSettings.defaultTags && (
              <p>
                <strong>Default Tags:</strong> {connectionSettings.defaultTags}
              </p>
            )}
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          You can modify these settings anytime from the integrations page.
        </p>
      </div>

      <DialogFooter>
        <Button
          onClick={() => {
            onOpenChange(false);
            onSuccess();
            resetDialog();
          }}
        >
          Done
        </Button>
      </DialogFooter>
    </>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetDialog();
        onOpenChange(isOpen);
      }}
    >
      <DialogContent className="max-w-4xl">
        {currentStep === ConnectionStep.CREDENTIALS && renderCredentialsStep()}
        {currentStep === ConnectionStep.BLOG_SELECTION &&
          renderBlogSelectionStep()}
        {currentStep === ConnectionStep.CONNECTED && renderConnectedStep()}
      </DialogContent>
    </Dialog>
  );
}
